import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import mapboxgl from 'mapbox-gl';
import PropTypes from 'prop-types';
import React from 'react';
import MapGL, { GeolocateControl, Marker, NavigationControl, useControl } from 'react-map-gl';
import { useMediaQuery } from 'react-responsive';

import { mediumUp } from 'constants/mediaQueries';
import { SHOP_IDS } from 'constants/shops';
import Environment from 'utils/Environment';
import Settings from 'utils/Settings';

import {
  GEO_COORDS_DEFAULT,
  MAP_STYLE,
  MAP_ZOOM_LEVELS,
  MAPBOX_PADDING,
  MAPBOX_TOKENS,
} from './constants';
import Pin from './Pin';

const Map = ({ mapRef, onGeocoderResult, onMarkerClick, selectedStore, stores }) => {
  const IS_DESKTOP = useMediaQuery({ query: mediumUp });
  const PADDING = IS_DESKTOP ? MAPBOX_PADDING.DESKTOP : MAPBOX_PADDING.MOBILE;
  const MAXPBOX_PROD_TOKEN =
    Settings.shopID === SHOP_IDS.US ? MAPBOX_TOKENS.US : MAPBOX_TOKENS.GLOBAL;
  const MAPBOX_TOKEN = Environment.isDev ? MAPBOX_TOKENS.DEV : MAXPBOX_PROD_TOKEN;

  return (
    <MapGL
      className="MapGL absolute inset-0"
      initialViewState={{
        longitude: GEO_COORDS_DEFAULT.center[0],
        latitude: GEO_COORDS_DEFAULT.center[1],
        zoom: IS_DESKTOP ? 4 : 2,
      }}
      padding={selectedStore ? PADDING : 0}
      mapStyle={MAP_STYLE}
      mapboxAccessToken={MAPBOX_TOKEN}
      projection="mercator"
      ref={mapRef}
      reuseMaps
    >
      <GeocoderControl onGeocoderResult={onGeocoderResult} mapboxAccessToken={MAPBOX_TOKEN} />
      <GeolocateControl
        fitBoundsOptions={{
          maxZoom: MAP_ZOOM_LEVELS.DETAIL,
        }}
        onGeolocate={onGeocoderResult}
        position="top-right"
        reverseGeocode
      />
      <NavigationControl position="top-right" />

      {stores.map(store => (
        /* istanbul ignore next */
        <Marker
          className={selectedStore?.handle === store?.handle ? 'z-20' : 'z-[1]'}
          key={store?.handle}
          latitude={store?.geoLocation?.lat}
          longitude={store?.geoLocation?.lon}
          onClick={() => onMarkerClick(store)}
        >
          <Pin isActive={selectedStore?.handle === store?.handle} store={store} />
        </Marker>
      ))}
    </MapGL>
  );
};

/* istanbul ignore next */
export const GeocoderControl = ({ onGeocoderResult, mapboxAccessToken }) => {
  useControl(
    () => {
      const ctrl = new MapboxGeocoder({
        accessToken: mapboxAccessToken,
        mapboxgl,
        marker: true,
      });
      ctrl.on('result', onGeocoderResult);
      return ctrl;
    },
    {
      position: 'top-left',
    }
  );
  return null;
};

GeocoderControl.propTypes = {
  onGeocoderResult: PropTypes.func.isRequired,
  mapboxAccessToken: PropTypes.string.isRequired,
};

Map.propTypes = {
  onGeocoderResult: PropTypes.func.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  selectedStore: PropTypes.object,
  stores: PropTypes.array.isRequired,
  mapRef: PropTypes.shape({
    current: PropTypes.instanceOf(Object),
  }).isRequired,
};

export default Map;
